import styles from './Invoices.module.css';

import config from 'config.js';
import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { CSVLink, CSVDownload } from 'react-csv';
import moment from 'moment-timezone';
import { getData } from 'country-list';
import {
  PageHeader,
  Card,
  Spin,
  Table,
  Button,
  DatePicker,
  Space,
  Row,
  Col,
  Statistic,
  Typography,
  Modal,
  Input,
  Form,
  InputNumber
} from 'antd';

import { FilePdfOutlined, ReloadOutlined, DownloadOutlined, EditOutlined, DeliveredProcedureOutlined } from '@ant-design/icons';

const euContries = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'RU',
  'SK',
  'SI',
  'ES',
  'SE'
];

const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const Invoices = () => {
  const [date, setDate] = useState(moment.tz(new Date(), 'America/New_York'));
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('stripe');
  const [stripeInvoices, setStripeInvoices] = useState([]);
  const [ltdf2021Invoices, setLtdf2021Invoices] = useState([]);
  const [editedInvoiceIndex, setEditedInvoiceIndex] = useState(null);

  const data = useMemo(() => (source === 'stripe' ? stripeInvoices : ltdf2021Invoices), [source, stripeInvoices, ltdf2021Invoices]);

  const countries = useMemo(() => {
    let list = {};
    const data = getData();

    for (let obj of data) list[obj.code] = obj.name;

    return list;
  }, [getData]);

  const ltdf2021InvoicesCsvData = useMemo(() => {
    let csv = [];

    for (let item of ltdf2021Invoices) {
      csv.push({
        Numer: item.number,
        NazwaKontrahenta: item.name || item.email,
        EmailKontrahenta: item.email,
        NipKontrahenta: item.vatEu,
        KrajKontrahenta: countries[item.address.country],
        KodKrajuKontrahenta: item.address.country,
        KontrahentEu: euContries.indexOf(item.address.country) !== -1 ? 'tak' : 'nie',
        TypKontrahenta: item.address.type === 'individual' ? 'osoba fizyczna' : 'firma',
        AdresKontrahenta: `${item.address.address1 ? item.address.address1 + ', ' : ''}${
          item.address.postal ? item.address.postal + ' ' : ''
        }${item.address.city ? item.address.city + ', ' : ''}${countries[item.address.country]}`,
        DataWystawienia: item.date,
        DataSprzedazy: item.date,
        LiczbaWierszySprzedazy: 1,
        OpisSprzedazy: item.items[0].description,
        NettoUsd: item.subtotalUsd || 0,
        BruttoUsd: item.totalUsd || 0,
        VatUsd: item.taxUsd || 0,
        NettoPln: item.subtotalPln || 0,
        BruttoPln: item.totalPln || 0,
        VatPln: item.taxPln || 0,
        Waluta: item.currency,
        StawkaVat: item.taxRate || 0,
        NumerNbp: item.nbpNo,
        DataNbp: item.nbpEffectiveDate,
        PrzelicznikNbp: item.nbpMid
      });
    }

    return csv;
  }, [ltdf2021Invoices]);

  const updateEditedInvoiceIndex = (number, from) => {
    const localData = from === 'stripe' ? stripeInvoices : ltdf2021Invoices;
    setSource(from);
    setEditedInvoiceIndex(localData.findIndex((item) => item.number === number));
  };

  const stripeColumns = [
    {
      title: 'Number',
      dataIndex: 'number',
      key: 'number',
      sorter: (a, b) => a.number - b.number
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName'
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date)
    },
    {
      title: 'Subtotal USD',
      dataIndex: 'subtotalUsd',
      key: 'subtotalUsd',
      sorter: (a, b) => a.subtotalUsd - b.subtotalUsd
    },
    {
      title: 'Total USD',
      dataIndex: 'totalUsd',
      key: 'totalUsd',
      sorter: (a, b) => a.totalUsd - b.totalUsd
    },
    {
      title: 'Tax USD',
      dataIndex: 'taxUsd',
      key: 'taxUsd',
      sorter: (a, b) => a.taxUsd - b.taxUsd
    },
    {
      title: 'Percent Off',
      dataIndex: 'percentOff',
      key: 'percentOff',
      sorter: (a, b) => a.percentOff - b.percentOff
    },
    {
      title: 'Discount USD',
      dataIndex: 'discountUsd',
      key: 'discountUsd',
      sorter: (a, b) => a.discountUsd - b.discountUsd
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'taxRate',
      sorter: (a, b) => a.taxRate - b.taxRate,
      render: (text) => `${text}%`
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record, index) => (
        <Space size="small">
          <Button
            type="primary"
            shape="circle"
            onClick={() => updateEditedInvoiceIndex(text.number, 'stripe')}
            icon={<EditOutlined />}
            size="small"
          />
          <Button
            type="primary"
            shape="circle"
            onClick={() => {
              setSource('stripe');
              generateInvoice([index], 'stripe');
            }}
            icon={<DeliveredProcedureOutlined />}
            size="small"
          />
        </Space>
      )
    }
  ];

  const ltdf2021Columns = [
    {
      title: 'Number',
      dataIndex: 'number',
      key: 'number',
      sorter: (a, b) => a.number - b.number
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date)
    },
    {
      title: 'Subtotal USD',
      dataIndex: 'subtotalUsd',
      key: 'subtotalUsd',
      sorter: (a, b) => a.subtotalUsd - b.subtotalUsd
    },
    {
      title: 'Total USD',
      dataIndex: 'totalUsd',
      key: 'totalUsd',
      sorter: (a, b) => a.totalUsd - b.totalUsd
    },
    {
      title: 'Tax USD',
      dataIndex: 'taxUsd',
      key: 'taxUsd',
      sorter: (a, b) => a.taxUsd - b.taxUsd
    },
    {
      title: 'Percent Off',
      dataIndex: 'percentOff',
      key: 'percentOff',
      sorter: (a, b) => a.percentOff - b.percentOff
    },
    {
      title: 'Discount USD',
      dataIndex: 'discountUsd',
      key: 'discountUsd',
      sorter: (a, b) => a.discountUsd - b.discountUsd
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'taxRate',
      sorter: (a, b) => a.taxRate - b.taxRate,
      render: (text) => `${text}%`
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record, index) => (
        <Space size="small">
          <Button
            type="primary"
            shape="circle"
            onClick={() => updateEditedInvoiceIndex(text.number, 'ltdf2021')}
            icon={<EditOutlined />}
            size="small"
          />
          <Button
            type="primary"
            shape="circle"
            onClick={() => {
              setSource('ltdf2021');
              generateInvoice([index], 'ltdf2021');
            }}
            icon={<DeliveredProcedureOutlined />}
            size="small"
          />
        </Space>
      )
    }
  ];

  const getStripeData = async () => {
    try {
      const response = await axios({
        method: 'POST',
        url: `${config.apiUrl}/root/invoices/stripe`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`
        },
        data: {
          start: date.startOf('month').toISOString(),
          end: date.endOf('month').toISOString(),
          month: date.format('M'),
          year: date.format('YYYY'),
          nbpStartDate: ((d) => d.startOf('month').subtract(5, 'days').format('YYYY-MM-DD'))(date.clone()),
          nbpEndDate: ((d) => {
            if (d.endOf('month').isAfter(new Date())) {
              return moment(new Date()).format('YYYY-MM-DD');
            } else {
              return d.endOf('month').format('YYYY-MM-DD');
            }
          })(date.clone())
        }
      });

      setStripeInvoices(response.data);

      return response.data;
    } catch (value) {
      return [];
    }
  };

  const getLtdf2021Data = async () => {
    try {
      const response = await axios({
        method: 'GET',
        url: `${config.apiUrl}/root/invoices/ltdf2021`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`
        },
        params: {
          start: date.startOf('month').toISOString(),
          end: date.endOf('month').toISOString(),
          nbpStartDate: ((d) => d.startOf('month').subtract(5, 'days').format('YYYY-MM-DD'))(date.clone()),
          nbpEndDate: ((d) => {
            if (d.endOf('month').isAfter(new Date())) {
              return moment(new Date()).format('YYYY-MM-DD');
            } else {
              return d.endOf('month').format('YYYY-MM-DD');
            }
          })(date.clone())
        }
      });

      setLtdf2021Invoices(response.data);

      return response.data;
    } catch (value) {
      return [];
    }
  };

  const reload = async () => {
    setLoading(true);
    await getStripeData();
    await getLtdf2021Data();
    setLoading(false);
  };

  const updateInvoice = (key, value) => {
    let copy = [...data];

    copy[editedInvoiceIndex][key] = value;

    setStripeInvoices(copy);
  };

  const updateTaxRate = (value) => {
    let copy = [...data];

    let {
      subtotalPln,
      discountPln,
      taxPln,
      totalPln,

      subtotalUsd,
      discountUsd,
      taxUsd,
      totalUsd
    } = copy[editedInvoiceIndex];

    taxUsd = round((subtotalUsd - discountUsd) * (value / 100));
    totalUsd = round(subtotalUsd - discountUsd + taxUsd);

    taxPln = round((subtotalPln - discountPln) * (value / 100));
    totalPln = round(subtotalPln - discountPln + taxPln);

    copy[editedInvoiceIndex].taxUsd = taxUsd;
    copy[editedInvoiceIndex].totalUsd = totalUsd;

    copy[editedInvoiceIndex].taxPln = taxPln;
    copy[editedInvoiceIndex].totalPln = totalPln;

    copy[editedInvoiceIndex].taxRate = value;

    setStripeInvoices(copy);
  };

  const generateInvoice = async (indexes, from) => {
    setLoading(true);

    let number = 656;

    const localData = from === 'stripe' ? stripeInvoices : ltdf2021Invoices;

    for (let index of indexes) {
      if (from === 'ltdf2021') {
        localData[index].number = `LTDF2021-0${number}`
        number += 1;
      }

      await axios({
        method: 'POST',
        url: `${config.apiUrl}/root/invoices`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`
        },
        data: localData[index]
      });
    }

    setLoading(false);
  };

  const generateLtdfJpk = async (data) => {
    setLoading(true);

    /*
     * K_12 - firma z EU - kwota netto zwolnione z podatku (VAT EU)
     * K_11 - osoba fizyczna outside EU - kwota netto
     * K_19 - osoba fizyczna EU - kwota netto
     * K_20 - osoba fizyczna EU - kwota podatku należnego 23%
     */

    const items = data.map((item, index) => {
      const euMember = euContries.indexOf(item.KodKrajuKontrahenta) !== -1;

      let props = {
        'tns:LpSprzedazy': index + 1,
        'tns:NrKontrahenta': item.NipKontrahenta || 'BRAK',
        'tns:NazwaKontrahenta': item.NazwaKontrahenta || 'BRAK',
        'tns:AdresKontrahenta': item.AdresKontrahenta,
        'tns:DowodSprzedazy': item.Numer,
        'tns:DataWystawienia': item.DataWystawienia,
        'tns:DataSprzedazy': item.DataSprzedazy
      };

      if (item.NipKontrahenta && euMember) {
        props['tns:K_12'] = item.BruttoPln; // firma z EU - kwota netto zwolnione z podatku (VAT EU)
      } else if (euMember) {
        props['tns:K_19'] = item.NettoPln; // osoba fizyczna EU - kwota netto
        props['tns:K_20'] = item.VatPln; // osoba fizyczna EU - kwota podatku należnego 23%
      } else {
        props['tns:K_11'] = item.BruttoPln; // osoba fizyczna outside EU - kwota netto
      }

      return props;
    });

    await axios({
      method: 'POST',
      url: `${config.apiUrl}/root/invoices/jpk`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('user_token')}`
      },
      data: {
        type: 'ltdf',
        date: date.startOf('month').toISOString(),
        items
      }
    });

    setLoading(false);
  };

  const generateStripeJpk = async (data) => {
    setLoading(true);

    /*
     * K_12 - firma z EU - kwota netto zwolnione z podatku (VAT EU)
     * K_11 - osoba fizyczna outside EU - kwota netto
     * K_19 - osoba fizyczna EU - kwota netto
     * K_20 - osoba fizyczna EU - kwota podatku należnego 23%
     */

    const items = data.map((item, index) => {
      const euMember = euContries.indexOf(item.address.country) !== -1;
      const taxId = item.taxId || item.address.taxNumber;

      let props = {
        'tns:LpSprzedazy': index + 1,
        'tns:NrKontrahenta': taxId  || 'BRAK',
        'tns:NazwaKontrahenta': item.customerName || 'BRAK',
        'tns:AdresKontrahenta': `${item.address.address1 ? item.address.address1 + ' ' : ''}${
          item.address.address2 ? item.address.address2 + ' ' : ''
        }${item.address.city ? item.address.city + ' ' : ''}${item.address.postal ? item.address.postal + ', ' : ''}${
          countries[item.address.country]
        }`,
        'tns:DowodSprzedazy': item.number,
        'tns:DataWystawienia': item.date,
        'tns:DataSprzedazy': item.date
      };

      if (taxId && euMember) {
        props['tns:K_12'] = item.totalPln; // firma z EU - kwota netto zwolnione z podatku (VAT EU)
      } else if (euMember) {
        props['tns:K_19'] = item.subtotalPln; // osoba fizyczna EU - kwota netto
        props['tns:K_20'] = item.taxPln; // osoba fizyczna EU - kwota podatku należnego 23%
      } else {
        props['tns:K_11'] = item.totalPln; // osoba fizyczna outside EU - kwota netto
      }

      return props;
    });

    await axios({
      method: 'POST',
      url: `${config.apiUrl}/root/invoices/jpk`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('user_token')}`
      },
      data: {
        type: 'stripe',
        date: date.startOf('month').toISOString(),
        items
      }
    });

    setLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <>
      <PageHeader
        title="Invoices"
        style={{ border: '1px solid #f0f0f0', background: '#fff', margin: '0 0 15px 0' }}
        extra={[
          <DatePicker defaultValue={moment(date, 'MM/YYYY')} format={'MM/YYYY'} picker="month" onChange={(value) => setDate(value)} />,
          <Button onClick={() => reload()}>
            <ReloadOutlined />
          </Button>
        ]}
      />

      <Spin spinning={loading} size="large">
        <Card style={{ width: '100%', minHeight: 224, padding: '40px 0 0 0' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic title="Invoices" value={stripeInvoices.length} prefix={<FilePdfOutlined />} />
            </Col>
            <Col span={12}>
              <Statistic title="Total" value={stripeInvoices.reduce((a, b) => a + b.totalUsd, 0)} prefix="$" />
            </Col>
          </Row>
          <br />
          <br />
          <Typography.Title>Stripe</Typography.Title>
          <Table dataSource={stripeInvoices} columns={stripeColumns} scroll={{ y: 240 }} size="small" />
          <br />
          <br />
          <Button
            type="primary"
            icon={<DeliveredProcedureOutlined />}
            onClick={() =>
              generateInvoice(
                stripeInvoices.map((item, index) => index),
                'stripe'
              )
            }
            size="small">
            Generate PDF
          </Button>
          <br />
          <br />
          <Button type="primary" onClick={() => generateStripeJpk(stripeInvoices)} size="small">
            Generate JPK
          </Button>
          <br />
          <hr />
          <br />
          <Typography.Title>LTDF 2021</Typography.Title>
          <Table dataSource={ltdf2021Invoices} columns={ltdf2021Columns} scroll={{ y: 240 }} size="small" />
          <br />
          <br />
          <Button
            type="primary"
            icon={<DeliveredProcedureOutlined />}
            onClick={() =>
              generateInvoice(
                ltdf2021Invoices.map((item, index) => index),
                'ltdf2021'
              )
            }
            size="small">
            Generate PDF
          </Button>
          {/* <br /> */}
          {/* <CSVLink data={ltdf2021InvoicesCsvData}>Download CSV</CSVLink> */}
          <br />
          <br />
          <a href="https://s3.console.aws.amazon.com/s3/buckets/questionscout-invoices" target="_blank">
            S3 bucket
          </a>
          <br />
          <br />
          <Button type="primary" onClick={() => generateLtdfJpk(ltdf2021InvoicesCsvData)} size="small">
            Generate JPK
          </Button>
        </Card>
      </Spin>

      <Modal
        title="Edit Invoice"
        visible={editedInvoiceIndex !== null}
        onOk={() => setEditedInvoiceIndex(null)}
        onCancel={() => setEditedInvoiceIndex(null)}>
        {editedInvoiceIndex !== null && data[editedInvoiceIndex] && (
          <>
            <p>Rate: {data[editedInvoiceIndex].nbpMid}</p>
            <hr />
            <Form.Item label="number">
              <Input
                value={data[editedInvoiceIndex].number}
                onChange={(e) => updateInvoice('number', e.target.value)}
                placeholder="number"
              />
            </Form.Item>
            <Form.Item label="date">
              <Input value={data[editedInvoiceIndex].date} onChange={(e) => updateInvoice('date', e.target.value)} placeholder="date" />
            </Form.Item>
            <hr />
            USD
            <br />
            <Form.Item label="subtotalUsd">
              <InputNumber
                value={data[editedInvoiceIndex].subtotalUsd}
                onChange={(value) => updateInvoice('subtotalUsd', value)}
                placeholder="subtotalUsd"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="discountUsd">
              <InputNumber
                value={data[editedInvoiceIndex].discountUsd}
                onChange={(value) => updateInvoice('discountUsd', value)}
                placeholder="discountUsd"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="taxUsd">
              <InputNumber
                value={data[editedInvoiceIndex].taxUsd}
                onChange={(value) => updateInvoice('taxUsd', value)}
                placeholder="taxUsd"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="totalUsd">
              <InputNumber
                value={data[editedInvoiceIndex].totalUsd}
                onChange={(value) => updateInvoice('totalUsd', value)}
                placeholder="totalUsd"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <hr />
            PLN
            <br />
            <Form.Item label="subtotalPln">
              <InputNumber
                value={data[editedInvoiceIndex].subtotalPln}
                onChange={(value) => updateInvoice('subtotalPln', value)}
                placeholder="subtotalPln"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="discountPln">
              <InputNumber
                value={data[editedInvoiceIndex].discountPln}
                onChange={(value) => updateInvoice('discountPln', value)}
                placeholder="discountPln"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="taxPln">
              <InputNumber
                value={data[editedInvoiceIndex].taxPln}
                onChange={(value) => updateInvoice('taxPln', value)}
                placeholder="taxPln"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="totalPln">
              <InputNumber
                value={data[editedInvoiceIndex].totalPln}
                onChange={(value) => updateInvoice('totalPln', value)}
                placeholder="totalPln"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <hr />
            <Form.Item label="percentOff">
              <InputNumber
                value={data[editedInvoiceIndex].percentOff}
                onChange={(value) => updateInvoice('percentOff', value)}
                placeholder="percentOff"
                readOnly={true}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label="taxRate">
              <InputNumber value={data[editedInvoiceIndex].taxRate} onChange={(value) => updateTaxRate(value)} placeholder="taxRate" />
            </Form.Item>
          </>
        )}
      </Modal>
    </>
  );
};

export default Invoices;
